import "@mappedin/mappedin-js/lib/index.css";
import { Label, MapView, useMap, useMapData } from "@mappedin/react-sdk";
import { FloorSelector } from "@mappedin/react-ui/components/floor-selector";
import { ZoomControls } from "@mappedin/react-ui/components/zoom-controls";
import "@mappedin/react-ui/themes/mappedin-dark.css";
import "./.mappedin__MappedInAirport.css";

import React, { useEffect, useState } from "react";

const isValidSpace = (space: any) =>
  space &&
  typeof space === "object" &&
  space.id &&
  space.center &&
  space.name !== undefined &&
  space.name !== null &&
  String(space.name).trim() !== "";

function MapContent() {
  const { mapData, mapView } = useMap();

  const floors = mapData.getByType("floor") ?? [];
  const validFloors = floors.filter((f: any) => f && f.id);
  const [currentFloor, setCurrentFloor] = useState<any | null>(null);

  const spaces = (mapData.getByType("space") ?? []).filter(isValidSpace);

  // Pick first valid floor after load
  useEffect(() => {
    if (!currentFloor && validFloors.length > 0) {
      const terminalFloor =
        validFloors.find(
          (f: any) =>
            f.name &&
            typeof f.name === "string" &&
            f.name.toLowerCase().includes("terminal")
        ) ?? validFloors[0];

      setCurrentFloor(terminalFloor);
    }
  }, [validFloors, currentFloor]);
  // Floor set only when both mapView + floor are valid
  useEffect(() => {
    if (mapView && currentFloor && currentFloor.id) {
      mapView.setFloor(currentFloor);
    }
  }, [mapView, currentFloor]);

  // Assign stable colors to spaces once on load
  useEffect(() => {
    if (!mapView || spaces.length === 0) return;

    spaces.forEach((space: any) => {
      // Generate a deterministic color from the space id
      let hash = 0;
      const id = String(space.id);
      for (let i = 0; i < id.length; i++) {
        hash = id.charCodeAt(i) + ((hash << 5) - hash);
      }
      const color =
        "#" + ((hash >>> 0) & 0xffffff).toString(16).padStart(6, "0");

      mapView.updateState(space, {
        color,
        interactive: true,
        hoverColor: color,
      });
    });
  }, [mapView, spaces.length]);

  return (
    <>
      {/* Floor Selector */}
      {validFloors.length > 0 && currentFloor && (
        <div style={{ position: "absolute", top: 20, right: 20, zIndex: 1000 }}>
          <FloorSelector
            floors={validFloors}
            currentFloor={currentFloor}
            onFloorSelected={(floor: any) => {
              if (floor && floor.id) {
                setCurrentFloor(floor);
                mapView?.setFloor(floor);
              }
            }}
          />
        </div>
      )}

      {/* Zoom Controls */}
      <div
        style={{ position: "absolute", bottom: 20, right: 20, zIndex: 1000 }}
      >
        <ZoomControls
          onZoomIn={() => {
            if (!mapView?.Camera) return;
            mapView.Camera.animateTo({
              zoomLevel: (mapView.Camera.zoomLevel ?? 0) + 1,
            });
          }}
          onZoomOut={() => {
            if (!mapView?.Camera) return;
            mapView.Camera.animateTo({
              zoomLevel: (mapView.Camera.zoomLevel ?? 0) - 1,
            });
          }}
        />
      </div>

      {/* Space Labels */}
      {spaces.map((space: any) => (
        <Label
          key={space.id}
          target={space.center}
          text={String(space.name ?? "")}
          options={{ interactive: true }}
        />
      ))}
    </>
  );
}

const MappedInAirport: React.FC = () => {
  const { isLoading, error, mapData } = useMapData({
    key: process.env.REACT_APP_MAPPEDIN_KEY!,
    secret: process.env.REACT_APP_MAPPEDIN_VALUE!,
    mapId: process.env.REACT_APP_MAPPEDIN_MAP_ID!,
  });

  if (isLoading) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "18px",
          fontWeight: 500,
          color: "#ffffff",
          backgroundColor: "#1a1a1a",
        }}
      >
        Loading Mappedin 3D map...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#ff6b6b",
          backgroundColor: "#2d2d2d",
          padding: "20px",
        }}
      >
        <div style={{ fontSize: "20px", fontWeight: 600 }}>
          Error Loading Map
        </div>
        <div>{error.message}</div>
      </div>
    );
  }

  return mapData ? (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
      }}
    >
      <MapView mapData={mapData} style={{ width: "100%", height: "100%" }}>
        <MapContent />
      </MapView>
    </div>
  ) : null;
};

export default MappedInAirport;
